import React, { useContext, SyntheticEvent, FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

// MUI
import Box from "@mui/material/Box";
import DialogActions from "@mui/material/DialogActions";
import Toolbar from "@mui/material/Toolbar";

// MUI Icons
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";

// Custom Components
import useFormController from "@/components/useFormController";
import DynamicDialog from "@/ui/Dialog/DynamicDialog";
import DialogHeader from "@/ui/Dialog/DialogHeader";
import DialogToolbarHeading from "@/ui/Dialog/DialogToolbarHeading";
import DialogToolbarButtonClose from "@/ui/Dialog/ToolbarButtons/DialogToolbarButtonClose";
import ToolbarFillContent from "@/ui/Toolbar/ToolbarFillContent";
import DialogBody from "@/ui/Dialog/DialogBody";
import DialogActionButtonClose from "@/ui/Dialog/ActionButtons/DialogActionButtonClose";
import DialogActionButtonAdd from "@/ui/Dialog/ActionButtons/DialogActionButtonAdd";
import DialogActionButtonSave from "@/ui/Dialog/ActionButtons/DialogActionButtonSave";
import DialogToolbarMenuButton from "@/ui/Dialog/DialogToolbarMenuButton";
import DialogToolbarMenuItem from "@/ui/Dialog/DialogToolbarMenuItem";
import DialogContext from "@/ui/DialogContext/DialogContext";
import SnackbarContext from "@/ui/SnackbarContext/SnackbarContext";
import { GridContainer, GridItem } from "@/ui/Grid";
import FormContent from "@/components/FormContent";

// Types
import { ClosingDetails, FormControllerProps } from "@/@types/components/formController";
import { IDialogProps } from "@/@types/ui/DialogContext";

const PlaninarskaUdrugaDialog: FunctionComponent<IDialogProps> = (props) => {
  const dialogContext = useContext(DialogContext);
  const snackbarContext = useContext(SnackbarContext);
  const { t } = useTranslation();

  const { dc, mode, form, onClose } = props;

  const {
    record,
    validation,
    dataChanged,
    fields,
    subModels,
    onFieldChange,
    doInsert,
    doUpdate,
    doClose,
    doDelete,
    doRefresh,
    doActivate
  } = useFormController(props as FormControllerProps);

  const handleFieldChange = (value: any, source: string) => {
    if (onFieldChange) {
      onFieldChange(value, source);
    }
  };

  const handleInsert = (evt: SyntheticEvent) => {
    if (doInsert) {
      doInsert().then((result) => {
        if (result.success) {
          const msg = t("notifications.insert");
          snackbarContext.showNotification(msg, "success");
          close({ dataChanged: true, action: "insert" });
        } else if (result.error) {
          const msg = t("error.addRecord");
          snackbarContext.showNotification(msg, "error");
        } else if (result.validationPass === false) {
          // do nothing
        }
      })
      .catch((resp) => {
        // wont ever be triggered cuz handleInsert never rejects promise
        snackbarContext.showNotification(resp.error, "error");
      });
    }
  };

  const handleUpdate = (evt: SyntheticEvent) => {
    if (doUpdate) {
      doUpdate().then((result) => {
        if (result.success) {
          const msg = t("notifications.update");
          snackbarContext.showNotification(msg, "success");
          close({ dataChanged: true, action: "update" });
        } else if (result.error) {
          // will not be triggered cauz handleUpdate rejects errors (so the catch under here will be triggered)
          const msg = t("error.editRecord");
          snackbarContext.showNotification(msg, "error");
        } else if (result.validationPass === false) {
          // do nothing
        }
      })
      .catch((resp) => {
        const msg = t("error.editRecord");
        snackbarContext.showNotification(msg, "error");
      });
    }
  };

  const handleClose = (evt: SyntheticEvent) => {
    if (doClose) {
      doClose().then((result) => {
        if (result.success) {
          close({ dataChanged: false, action: "cancel" });
        } else {
          if (result.shouldSave) {
            handleUpdate(evt);
          } else if (result.canceled) {
            // do nothing
          }
        }
      }).catch(e => console.log(e));
    }
  };

  const handleActivate = (evt: SyntheticEvent) => {
    if (record.id !== null) {
      try {
        doActivate(true).then((result) => {
          if (result) {
            const msg = t("notifications.activate");
            snackbarContext.showNotification(msg, "success");
            doRefresh();
            onClose({ dataChanged: true, action: "update" });
          } else {
            const msg = t("error.activate");
            snackbarContext.showNotification(msg, "error");
          }
        });
      } catch (error) {
        const msg = t("error.activate");
        snackbarContext.showNotification(msg, "error");
      }
    }
  };

  const handleDeactivate = (evt: SyntheticEvent) => {
    if (record.id !== null) {
      try {
        doActivate(false).then((result) => {
          if (result) {
            const msg = t("notifications.deactivate");
            snackbarContext.showNotification(msg, "success");
            close({ dataChanged: true, action: "update" });
          } else {
            const msg = t("error.deactiavate");
            snackbarContext.showNotification(msg, "error");
          }
        });
      } catch (error) {
        const msg = t("error.deactivate");
        snackbarContext.showNotification(msg, "error");
      }
    }
  };


  const close = (result: ClosingDetails) => {
    if (result.dataChanged || dataChanged) {
      onClose({ dataChanged: true, action: result.action });
    }
    dialogContext.hideDialog();
  };

  const handleDelete = (evt: SyntheticEvent) => {
    if (doDelete) {
      doDelete().then((result) => {
        if (result.success) {
          const msg = t("notifications.delete");
          snackbarContext.showNotification(msg, "success");
          close({ dataChanged: true, action: "update" });
        } else if (result.error) {
          const msg = t("error.delete") + result.error;
          snackbarContext.showNotification(msg, "error");
        }
      });
    }
  };

  let cmode = mode;
  if (record.active === false) {
    cmode = 'view';
  }

  return (
    <DynamicDialog open maxWidth="sm" onClose={handleClose}>
      <DialogHeader>
        <Toolbar variant="dense" disableGutters>
          <DialogToolbarHeading>{t("titles.plan_udruga")}</DialogToolbarHeading>
          <ToolbarFillContent />
          {mode === "update" ? (
            <DialogToolbarMenuButton>
              {record.active ? (
                <DialogToolbarMenuItem
                  onClick={handleDeactivate}
                  label={t("buttons.deactivate")}
                  icon={<CancelIcon />}
                />
              ) : null}
              {!record.active ? (
                <DialogToolbarMenuItem
                  onClick={handleActivate}
                  label={t("buttons.activate")}
                  icon={<CheckCircleIcon />}
                />
              ) : null}
              <DialogToolbarMenuItem
                onClick={handleDelete}
                label={t("buttons.delete")}
                icon={<DeleteForeverIcon />}
              />
            </DialogToolbarMenuButton>
          ) : null}
          <DialogToolbarButtonClose onClick={handleClose} />
        </Toolbar>
      </DialogHeader>
      <DialogBody>
        <Box m={2}>
          <GridContainer>
            <GridItem size={12}>
              <GridContainer>
                <FormContent
                  fieldNames={dc.getFormFieldsNames(form)}
                  record={record}
                  validation={validation}
                  fields={fields}
                  onFieldChange={handleFieldChange}
                  mode={cmode}
                  columns={1}
                  subModels={subModels}
                />
              </GridContainer>
            </GridItem>
          </GridContainer>
        </Box>
      </DialogBody>
      <DialogActions>
        <DialogActionButtonClose variant="outlined" onClick={handleClose} />
        {cmode === "insert" ? (
          <DialogActionButtonAdd variant="contained" onClick={handleInsert} />
        ) : cmode === "update" ? (
          <DialogActionButtonSave variant="contained" onClick={handleUpdate} />
        ) : null}
      </DialogActions>
    </DynamicDialog>
  );
};

export default PlaninarskaUdrugaDialog;
